/**
 * Task 55 · the document head's app identity, in one place.
 *
 * Frame 08 splits the delivery in two because the platforms are different, and
 * the two halves have very different risk:
 *
 * - **Android and Edge — the manifest.** `<link rel="manifest">` is rewritten to
 *   carry the tenant, `/manifest.webmanifest?e=<token>`, which a Netlify Edge
 *   Function answers per tenant. The SPEC calls this the «sikker vei; bygges
 *   uansett», and it is safe for a concrete reason: an installed Android app
 *   re-reads its manifest and corrects itself within days.
 *
 * - **iOS and the Dock — the document head.** `apple-touch-icon`,
 *   `apple-mobile-web-app-title` and `theme-color` swapped on branded surfaces.
 *   This one rests on an assumption nobody has tested: that Safari reads the
 *   head at the MOMENT the user taps «Legg til på Hjem-skjerm», not at page
 *   load. That is task 39, it needs a physical iPhone, and it is the last task
 *   in the plan.
 *
 * ## The iOS half shipped switched OFF, and 17.08.2026 is why that mattered
 *
 * Decided 06.08.2026: on iOS the icon is **frozen at install** — the SPEC's own
 * «nettopp plattformen der feil ikon er permanent» — so a swap resting on an
 * untested assumption was not worth its downside, and the manifest half went
 * out alone.
 *
 * It went out and measured green, and the icon was still wrong. David added a
 * Nortura event to his home screen on 16.08.2026 and got EventRipple's mark.
 * Measured against production the next morning:
 * `/manifest.webmanifest?e=<token>` answers «PROFF Event» with Nortura's icon,
 * and the same page's head says `apple-touch-icon: /apple-touch-icon.png`.
 * **iOS reads the head, not the manifest, whenever an `apple-touch-icon` is
 * present** — and one is, hardcoded in `index.html`. The Android half was
 * correct and was never the half being read.
 *
 * ## What changed, and why the switch is now ON
 *
 * `netlify/edge-functions/event-app-head.ts` writes the same four values into
 * the HTML the server delivers for `/event/*`, using `lib/app-head.ts`. **That
 * removes the assumption rather than testing it:** if the right icon is already
 * in the bytes Safari loaded, «at load» and «at share time» read the same
 * value, and both hypotheses land on the right mark.
 *
 * So this swap is no longer a coin flip standing alone — it is the half that
 * covers what the edge cannot see: navigation INSIDE the app, where no document
 * is fetched. Under the load-time hypothesis it is inert; under the share-time
 * hypothesis it is what makes `/events` → a Nortura event correct.
 *
 * One case remains wrong and is stated rather than hidden: load-time reading
 * AND a viewer crossing from one tenant's event to another's without a page
 * load. That needs two tenants and one device — measured 16.08.2026, exactly
 * one of Nortura's 84 participants is in more than one tenant.
 *
 * Task 39's fixture is still worth running (2 minutes, any iPhone); nothing
 * waits on it any more.
 */

/**
 * Task 39's switch. ON since 17.08.2026 — see above. It is **not** the thing
 * that makes iOS correct on its own: `event-app-head.ts` is, and this covers
 * client-side navigation on top of it. Turning it off is safe and costs the
 * in-app navigation case.
 */
export const IOS_HEAD_SWAP_ENABLED = true

export interface TenantAppIdentity {
  /**
   * The query the manifest endpoint is asked with, WITHOUT the `?`.
   * `e=<invitation-token>` from a public event surface, `t=<org-slug>` from a
   * tenant portal. Both resolve to the same tenant key, so a tenant reached
   * both ways installs one app (Følge 3).
   */
  manifestQuery: string
  /** The tenant's own square icon. `null` means «use EventRipple's», never «guess». */
  appIconUrl: string | null
  /** Max 12 characters — the name under the icon on a home screen. */
  shortName: string | null
  /** `#RRGGBB`. */
  themeColor: string | null
}

const MANIFEST_PATH = '/manifest.webmanifest'

/**
 * Set on `<html>`, to the icon URL, while the page's manifest link carries a
 * tenant that has an icon of its OWN. Task 54's install banner reads it, and
 * the wording matters: it does not mean «a tenant is branding this page» (that
 * is `data-brand-scope`), it means «installing from here now installs the right
 * mark».
 *
 * Those two facts have to move together, which is why one module owns both. The
 * banner's rule since task 54 has been «a tenant brand is present → do not
 * offer», with a note that it becomes «brand present AND no icon of its own»
 * when 55 lands. This attribute is that second half — and because the SAME code
 * that rewrites the manifest publishes it, a surface can never advertise an
 * install whose manifest it did not actually change.
 *
 * **It carries the value rather than a flag** so the banner can show the mark
 * it is about to install. Frame 08's 1d is explicit that the banner wears the
 * customer's brand; a banner reading «EventRipple» over a button that installs
 * Nortura is a worse lie than not offering at all. An attribute rather than a
 * context because the publisher is an effect in a hook and the reader is a
 * component mounted above it in `App.tsx` — a provider between them would have
 * to wrap the whole app to solve a two-string problem.
 */
export const TENANT_APP_ICON_ATTR = 'data-tenant-app-icon'

/** The name shown under that icon, max 12 characters. Set with the icon, never alone. */
export const TENANT_APP_NAME_ATTR = 'data-tenant-app-name'

/** Restores everything this module touched, in reverse. */
type Revert = () => void

export function setAttrReversible(
  el: Element | null,
  attr: string,
  value: string | null,
): Revert | null {
  if (!el || !value) return null
  const had = el.hasAttribute(attr)
  const previous = el.getAttribute(attr)
  el.setAttribute(attr, value)
  return () => {
    if (had && previous !== null) el.setAttribute(attr, previous)
    else el.removeAttribute(attr)
  }
}

/**
 * Point the page's app identity at a tenant. Returns the undo.
 *
 * Call it from an effect and return the result as the cleanup, the same shape
 * `usePublicEventBrand` uses for the CSS variables — a branded surface must not
 * leave its mark on the next route.
 *
 * **It rewrites the EXISTING `<link rel="manifest">` and never adds a second
 * one.** Two manifest links is undefined behaviour: the spec says the first is
 * used, browsers disagree in practice, and the one that loses is invisible.
 * VitePWA injects exactly one at build time (verified in `dist/index.html`); if
 * some future build stops injecting it, this does nothing rather than inventing
 * an element — the tenant then gets the EventRipple default, which is today's
 * behaviour and not a regression.
 */
export function applyTenantAppIdentity(identity: TenantAppIdentity | null): Revert {
  if (!identity) return () => {}
  const reverts: Revert[] = []

  const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')
  const r = setAttrReversible(link, 'href', `${MANIFEST_PATH}?${identity.manifestQuery}`)
  if (r) reverts.push(r)

  // Only when the rewrite actually happened AND the tenant has a mark of its
  // own. Either half missing means the install would still fetch EventRipple's
  // icon, and task 54's guard must keep the banner shut.
  if (r && identity.appIconUrl) {
    const root = document.documentElement
    const iconFlag = setAttrReversible(root, TENANT_APP_ICON_ATTR, identity.appIconUrl)
    if (iconFlag) reverts.push(iconFlag)
    // The name falls back to nothing rather than to «EventRipple»: the banner
    // renders the icon alone if a tenant has a mark but no short name, and an
    // icon with the wrong name under it is the exact mistake this task exists
    // to stop.
    const nameFlag = setAttrReversible(root, TENANT_APP_NAME_ATTR, identity.shortName)
    if (nameFlag) reverts.push(nameFlag)
  }

  // `identity.appIconUrl` gates the whole block, exactly as it gates the two
  // attributes above, and the gate is load-bearing now that the switch is on.
  // `useEventAppIdentity` publishes a tenant with a NULL icon on purpose — the
  // manifest endpoint answers with the EventRipple default until the concierge
  // sets a mark, with no deploy. Without this condition that tenant would swap
  // the name and the colour and leave the icon, and «EventRipple's mark with
  // PROFF Event under it» is the one outcome task 54's guard names — and on iOS
  // it is frozen at install.
  if (IOS_HEAD_SWAP_ENABLED && identity.appIconUrl) {
    reverts.push(
      ...([
        setAttrReversible(
          document.querySelector('link[rel="apple-touch-icon"]'),
          'href',
          identity.appIconUrl,
        ),
        setAttrReversible(
          document.querySelector('meta[name="apple-mobile-web-app-title"]'),
          'content',
          identity.shortName,
        ),
        setAttrReversible(
          document.querySelector('meta[name="theme-color"]'),
          'content',
          identity.themeColor,
        ),
      ].filter(Boolean) as Revert[]),
    )
  }

  return () => {
    for (let i = reverts.length - 1; i >= 0; i--) reverts[i]()
  }
}

/* ==========================================================================
 * ONE OWNER FOR THE LINK
 * ========================================================================== */

/**
 * Who is asking. There is exactly one `<link rel="manifest">` on the page, and
 * as of 13.08.2026 there are two things with an opinion about it:
 *
 * - **`surface`** — the tenant that owns the page you are standing on, learned
 *   from an invitation token by `usePublicEventBrand`.
 * - **`account`** — the tenant the signed-in viewer belongs to, learned from
 *   `get_app_identity_for_me` by `useTenantAppIdentity`.
 *
 * Until now only the first existed, so a plain effect was enough. Two effects
 * writing one attribute is a race whose loser is invisible: whichever resolved
 * last wins, both hold a revert that restores what THEY saw, and unmount order
 * decides what the page is left wearing.
 */
export type IdentitySlot = 'surface' | 'account'

const slots: Record<IdentitySlot, TenantAppIdentity | null> = {
  surface: null,
  account: null,
}

let applied: TenantAppIdentity | null = null
let revertApplied: Revert = () => {}

/** Cheap structural equality — the fields are four strings-or-null. */
function same(a: TenantAppIdentity | null, b: TenantAppIdentity | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.manifestQuery === b.manifestQuery &&
    a.appIconUrl === b.appIconUrl &&
    a.shortName === b.shortName &&
    a.themeColor === b.themeColor
  )
}

/**
 * Publish this slot's opinion and re-resolve. **`surface` wins over `account`.**
 *
 * That order is Følge 3 read literally — «appen følger tenanten som eier flaten
 * du står på». A Compass Group employee opening a Nortura event link is looking
 * at a Nortura page, and the install offered there is Nortura's. The common
 * case is that both slots name the same tenant, and then the order costs
 * nothing; it exists for the case where they disagree.
 *
 * Pass `null` to withdraw — always do this in an effect's cleanup, or a branded
 * surface leaves its manifest behind on the next route.
 */
export function publishTenantAppIdentity(
  slot: IdentitySlot,
  identity: TenantAppIdentity | null,
): void {
  slots[slot] = identity
  const next = slots.surface ?? slots.account
  // Re-applying an identical identity would revert and re-set the same
  // attributes for no reason, and the churn is observable: `InstallPrompt`
  // watches these attributes with a MutationObserver.
  if (same(next, applied)) return
  revertApplied()
  applied = next
  revertApplied = applyTenantAppIdentity(next)
}

/** Test seam — drops both slots and restores the document. */
export function resetTenantAppIdentity(): void {
  slots.surface = null
  slots.account = null
  revertApplied()
  applied = null
  revertApplied = () => {}
}
